
class CollaborationApi {
  constructor() {
    this._axios = null;
  }

  
  async _getAxios() {
    if (!this._axios) {
      this._axios = (await import('axios')).default;
    }
    return this._axios;
  }

  get baseURL() {
    return process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
  }

  get organizationId() {
    return localStorage.getItem('organizationId');
  }

  get headers() {
    // Auth handled via httpOnly cookies (withCredentials: true)
    return {};
  }


  get isAuthenticated() {
    // For cookie-based auth, cookies are sent automatically with each request
    // Let the server handle authentication - no need for frontend check
    return true;
  }


  async searchUsersAndTeams(query) {
    if (!this.isAuthenticated || !this.organizationId) return { users: [], teams: [] };

    const axios = await this._getAxios();
    const orgId = this.organizationId;

    const [usersRes, teamsRes] = await Promise.all([
      axios.get(`/api/organizations/${orgId}/users/search`, {
        params: { query },
        headers: this.headers,
        baseURL: this.baseURL
      }).catch(() => ({ data: { users: [] } })),
      axios.get(`/api/organizations/${orgId}/teams/search`, {
        params: { query },
        headers: this.headers,
        baseURL: this.baseURL
      }).catch(() => ({ data: { teams: [] } }))
    ]);

    const users = (usersRes.data.users || []).map(u => ({
      type: 'user',
      id: u.id,
      email: u.email,
      name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      displayText: `${u.firstName || ''} ${u.lastName || ''}`.trim()
        ? `${u.firstName} ${u.lastName} (${u.email})`
        : u.email
    }));

    const teams = (teamsRes.data.teams || []).map(t => ({
      type: 'team',
      id: t.id,
      name: t.name,
      memberCount: t._count?.members || 0,
      displayText: `${t.name} (${t._count?.members || 0} members)`
    }));

    return { users, teams };
  }


  async shareBulk(fileId, recipients, permission) {
    const axios = await this._getAxios();
    const response = await axios.post('/api/collaboration/share/bulk', {
      fileId,
      recipients,
      permission
    }, {
      headers: this.headers,
      baseURL: this.baseURL
    });
    return response.data;
  }


  async loadTeams() {
    if (!this.isAuthenticated || !this.organizationId) return [];

    const axios = await this._getAxios();
    const response = await axios.get(`/api/organizations/${this.organizationId}/teams`, {
      headers: this.headers,
      baseURL: this.baseURL
    });
    return response.data.teams || [];
  }


  async loadInvitations() {
    if (!this.isAuthenticated) return [];

    const axios = await this._getAxios();
    const response = await axios.get('/api/collaboration/invitations', {
      headers: this.headers,
      baseURL: this.baseURL
    });
    return response.data || [];
  }

  async respondToInvitation(collaborationId, response) {
    const axios = await this._getAxios();
    return axios.post(
      `/api/collaboration/invitations/${collaborationId}/respond`,
      { response },
      { headers: this.headers, baseURL: this.baseURL }
    );
  }

  // A visitor with a file's link but no access asks the owner for it.
  async requestAccess(fileId, message = '') {
    const axios = await this._getAxios();
    const res = await axios.post(
      '/api/collaboration/request-access',
      { fileId, message },
      { headers: this.headers, baseURL: this.baseURL }
    );
    return res.data;
  }

  // Owner's list of pending access requests across all their files.
  async loadAccessRequests() {
    const axios = await this._getAxios();
    const res = await axios.get('/api/collaboration/requests', { headers: this.headers, baseURL: this.baseURL });
    return res.data || [];
  }

  // Owner grants or denies one request ('grant' | 'deny').
  async respondToAccessRequest(collaborationId, response, permission = 'view') {
    const axios = await this._getAxios();
    const res = await axios.post(
      `/api/collaboration/requests/${collaborationId}/respond`,
      { response, permission },
      { headers: this.headers, baseURL: this.baseURL }
    );
    return res.data;
  }


  async loadPublicShares(fileId) {
    const axios = await this._getAxios();
    const response = await axios.get('/api/collaboration/public-shares', {
      params: { fileId },
      headers: this.headers,
      baseURL: this.baseURL
    });
    return response.data.shares || [];
  }

  async createPublicShare(payload) {
    const axios = await this._getAxios();
    const response = await axios.post('/api/collaboration/public-shares', payload, {
      headers: this.headers,
      baseURL: this.baseURL
    });
    return response.data;
  }

  async deletePublicShare(shareId) {
    const axios = await this._getAxios();
    return axios.delete(`/api/collaboration/public-shares/${shareId}`, {
      headers: this.headers,
      baseURL: this.baseURL
    });
  }

  async togglePublicShare(shareId, isActive) {
    const axios = await this._getAxios();
    return axios.put(`/api/collaboration/public-shares/${shareId}`, {
      isActive
    }, {
      headers: this.headers,
      baseURL: this.baseURL
    });
  }


  async syncFileToServer(fileData) {
    const axios = await this._getAxios();
    return axios.post('/api/files/sync', fileData, {
      headers: this.headers,
      baseURL: this.baseURL
    });
  }
}

const collaborationApi = new CollaborationApi();

export default collaborationApi;
export { CollaborationApi };
